import React, { useEffect, useState } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    Spinner,
    Badge,
    Table,
    Alert,
    Row,
    Col,
    Card,
    CardBody,
} from "reactstrap";
import { getFarmhouseBookingHistory } from "../../helpers/apis";

const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
};

const formatBookingType = (type) => {
    if (!type) return "N/A";
    return type.replace(/_/g, " ");
};

const getBookingStatusBadge = (status) => {
    const statusMap = {
        upcoming: { color: "primary", label: "Upcoming" },
        expired: { color: "secondary", label: "Expired" },
        cancelled: { color: "danger", label: "Cancelled" },
        completed: { color: "success", label: "Completed" },
    };
    const statusInfo = statusMap[status?.toLowerCase()] || { color: "info", label: status || "N/A" };
    return <Badge color={statusInfo.color}>{statusInfo.label}</Badge>;
};

const getPaymentStatusBadge = (status) => {
    const statusMap = {
        paid: { color: "success", label: "Paid" },
        incomplete: { color: "warning", label: "Incomplete" },
        partial: { color: "info", label: "Outstanding" },
    };
    const statusInfo = statusMap[status?.toLowerCase()] || { color: "secondary", label: status || "N/A" };
    return <Badge color={statusInfo.color}>{statusInfo.label}</Badge>;
};

const BookingHistoryModal = ({ isOpen, toggle, farmhouseId, farmhouseName }) => {
    const [loading, setLoading] = useState(false);
    const [bookingData, setBookingData] = useState(null);
    const [error, setError] = useState(null);
    const bookedDates = bookingData?.bookedDates || [];
    const totalBookings = bookingData?.totalBookings ?? bookedDates.length;

    const summary = React.useMemo(() => {
        let pendingPayment = 0;
        let totalIncome = 0;

        bookedDates.forEach((booking) => {
            const status = booking.paymentStatus?.toLowerCase();
            const finalPrice = Number(booking.finalPrice) || 0;
            const paidAmount = Number(booking.partialPayment?.paidAmount ?? booking.paidAmount ?? 0);
            const remainingAmount = Number(booking.partialPayment?.remainingAmount ?? booking.remainingAmount ?? 0);

            // Income: paid + partial paid amounts
            if (status === "paid") {
                totalIncome += finalPrice;
            } else if (status === "partial") {
                totalIncome += paidAmount;
            }

            // Pending: incomplete finalPrice, partial remainingAmount
            if (status === "incomplete") {
                pendingPayment += remainingAmount || finalPrice;
            } else if (status === "partial") {
                pendingPayment += remainingAmount;
            }
        });

        return {
            pendingPayment,
            totalIncome,
        };
    }, [bookedDates]);

    useEffect(() => {
        if (isOpen && farmhouseId) {
            fetchBookingHistory();
        } else {
            setBookingData(null);
            setError(null);
        }
    }, [isOpen, farmhouseId]);

    const fetchBookingHistory = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getFarmhouseBookingHistory(farmhouseId);
            if (!response.error) {
                setBookingData(response.data);
            } else {
                setError(response.msg || "Failed to fetch booking history");
            }
        } catch (err) {
            setError(err.message || "Failed to fetch booking history");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" centered className="full-height-model">
            <ModalHeader toggle={toggle}>
                Booking History - {farmhouseName || "Farmhouse"}
            </ModalHeader>
            <ModalBody style={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}>
                {loading ? (
                    <div className="text-center p-5">
                        <Spinner color="primary" />
                        <p className="mt-2">Loading booking history...</p>
                    </div>
                ) : error ? (
                    <Alert color="danger">{error}</Alert>
                ) : bookingData ? (
                    <div>
                        {bookingData.farmhouse && (
                            <div className="mb-4 p-3 border rounded" style={{ backgroundColor: '#f5f5f5' }}>
                                <h6 className="mb-2">Farmhouse Information</h6>
                                <div className="row">
                                    <div className="col-md-6">
                                        <strong>Name:</strong> {bookingData.farmhouse.name}
                                    </div>
                                    <div className="col-md-6">
                                        <strong>Farm No:</strong> {bookingData.farmhouse.farmNo}
                                    </div>
                                </div>
                            </div>
                        )}

                        <Row className="mb-4">
                            <Col md={4} className="mb-3">
                                <Card className="h-100 shadow-sm">
                                    <CardBody>
                                        <div className="text-muted text-uppercase fs-12">Pending Payment Amount</div>
                                        <h5 className="mb-0">₹{summary.pendingPayment.toLocaleString()}</h5>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col md={4} className="mb-3">
                                <Card className="h-100 shadow-sm">
                                    <CardBody>
                                        <div className="text-muted text-uppercase fs-12">Total Payment Income</div>
                                        <h5 className="mb-0">₹{summary.totalIncome.toLocaleString()}</h5>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col md={4} className="mb-3">
                                <Card className="h-100 shadow-sm">
                                    <CardBody>
                                        <div className="text-muted text-uppercase fs-12">Total Bookings</div>
                                        <h5 className="mb-0">{totalBookings}</h5>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>

                        {bookingData.bookedDates && bookingData.bookedDates.length > 0 ? (
                            <div className="table-responsive" style={{ overflowX: "auto", overflowY: "visible" }}>
                                <Table striped bordered hover style={{ marginBottom: 0 }}>
                                    <thead>
                                        <tr>
                                            <th>User</th>
                                            <th>Date</th>
                                            <th>Check-in/Check-out</th>
                                            <th>Persons</th>
                                            <th>Type</th>
                                            <th>Booking Status</th>
                                            <th>Payment Status</th>
                                            <th>Payment Summary</th>
                                            <th>Price</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bookingData.bookedDates.map((booking) => (
                                            <tr key={booking.id}>
                                                <td>
                                                    <div>
                                                        <strong>{booking.user?.name || "N/A"}</strong>
                                                        <br />
                                                        <small className="text-muted">
                                                            {booking.user?.email || "N/A"}
                                                        </small>
                                                        {booking.user?.mobileNo && booking.user.mobileNo !== "N/A" && (
                                                            <>
                                                                <br />
                                                                <small className="text-muted">
                                                                    {booking.user.mobileNo}
                                                                </small>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    {formatDate(booking.startDate)}
                                                    {booking.startDate !== booking.endDate && (
                                                        <span> - {formatDate(booking.endDate)}</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <div>
                                                        <small>
                                                            <strong>In:</strong> {booking.checkInTime || "N/A"}
                                                        </small>
                                                        <br />
                                                        <small>
                                                            <strong>Out:</strong> {booking.checkOutTime || "N/A"}
                                                        </small>
                                                    </div>
                                                </td>
                                                <td>{booking.numberOfPersons || "N/A"}</td>
                                                <td>{formatBookingType(booking.bookingType)}</td>
                                                <td>{getBookingStatusBadge(booking.bookingStatus)}</td>
                                                <td>{getPaymentStatusBadge(booking.paymentStatus)}</td>
                                                <td>
                                                    <div className="small">
                                                        <div><strong>Paid:</strong> ₹{Number(booking.partialPayment?.paidAmount ?? booking.paidAmount ?? 0).toLocaleString()}</div>
                                                        <div><strong>Remaining:</strong> ₹{Number(booking.partialPayment?.remainingAmount ?? booking.remainingAmount ?? 0).toLocaleString()}</div>
                                                        {booking.partialPayment?.notes && (
                                                            <div className="text-muted fst-italic">{booking.partialPayment.notes}</div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div>
                                                        <strong>₹{booking.finalPrice || 0}</strong>
                                                        {booking.discountAmount > 0 && (
                                                            <div>
                                                                <small className="text-muted">
                                                                    Original: ₹{booking.originalPrice}
                                                                </small>
                                                                <br />
                                                                <small className="text-success">
                                                                    Discount: ₹{booking.discountAmount}
                                                                </small>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        ) : (
                            <Alert color="info">No booking history found for this farmhouse.</Alert>
                        )}
                    </div>
                ) : (
                    <Alert color="warning">No data available</Alert>
                )}
            </ModalBody>
        </Modal>
    );
};

export default BookingHistoryModal;

